/**
 * Small formatting helpers shared across HR pages.
 */

import dayjs from 'dayjs';
import {
  IconFile,
  IconFileTypePdf,
  IconFileTypeDoc,
  IconFileTypeDocx,
  IconFileTypeXls,
  IconFileTypeCsv,
  IconFileTypePpt,
  IconFileTypeZip,
  IconFileTypeTxt,
  IconPhoto,
  IconFileMusic,
  IconVideo,
  type Icon,
} from '@tabler/icons-react';

// The backend already localizes to the recipient's timezone and emits an offset; render that
// wall-clock verbatim by stripping any trailing 'Z'/±hh:mm (no re-zoning to the browser). A naive
// string (e.g. a date-only "2026-06-24") has nothing to strip and is parsed in place.
const hrParse = (input: string | Date) =>
  typeof input === 'string' ? dayjs(input.replace(/(Z|[+-]\d{2}:?\d{2})$/, '')) : dayjs(input);

export const formatDate = (input: string | Date | null | undefined): string => {
  if (!input) return '—';
  const d = hrParse(input);
  return d.isValid() ? d.format('DD MMM YYYY') : '—';
};

export const formatDateTime = (input: string | Date | null | undefined): string => {
  if (!input) return '—';
  const d = hrParse(input);
  return d.isValid() ? d.format('DD MMM YYYY HH:mm') : '—';
};

export const formatDays = (n: number | null | undefined): string => {
  if (n == null) return '—';
  return n === 1 ? '1 day' : `${n} days`;
};

export const fullName = (first?: string | null, last?: string | null): string =>
  [first, last].filter(Boolean).join(' ').trim() || '—';

/**
 * User-facing display name for an EmployeeModelView. Never falls through to the
 * raw user ID — that's an internal identifier the staff doesn't recognise.
 * Priority: fullName → "name surname" → employeeNo → email → "Unnamed employee".
 */
export const employeeDisplayName = (e: {
  fullName?: string | null;
  name?: string | null;
  surname?: string | null;
  employeeNo?: string | null;
  email?: string | null;
}): string => {
  if (e.fullName?.trim()) return e.fullName.trim();
  const composed = [e.name, e.surname].filter(Boolean).join(' ').trim();
  if (composed) return composed;
  if (e.employeeNo?.trim()) return `Employee ${e.employeeNo.trim()}`;
  if (e.email?.trim()) return e.email.trim();
  return 'Unnamed employee';
};

/**
 * Build a Mantine `<Select data={...}>` array from a list of lookup rows.
 *
 * - Skips rows whose id is null/undefined (those would all serialise to the
 *   string `"undefined"` and Mantine 7 throws on duplicate option values).
 * - De-duplicates by id so the picker stays single-valued.
 *
 * Use this everywhere we feed an HR lookup (LeaveType, EmployeeType, etc.)
 * into a Select — defends against backend warmup gaps that can otherwise
 * crash the form drawer.
 */
export function lookupOptions<T>(
  rows: readonly T[] | undefined | null,
  getId: (row: T) => unknown,
  getLabel: (row: T) => string,
): { value: string; label: string }[] {
  const seen = new Set<string>();
  const out: { value: string; label: string }[] = [];
  for (const row of rows ?? []) {
    const id = getId(row);
    if (id === null || id === undefined || id === '') continue;
    const value = String(id);
    if (seen.has(value)) continue;
    seen.add(value);
    out.push({ value, label: getLabel(row) });
  }
  return out;
}

/**
 * Pick a file-type icon + Mantine colour for a file based on its MIME type or
 * filename extension. Used by the Files table/grid so each row gets a visual
 * cue for what kind of document it is (PDF, Word, Excel, image, etc.).
 *
 * Falls back to a generic file icon for anything we don't recognise.
 */
export const getFileIcon = (file: {
  mediaType?: string | null;
  fileName?: string | null;
}): { Icon: Icon; color: string } => {
  const mime = (file.mediaType ?? '').toLowerCase();
  const ext = (file.fileName?.split('.').pop() ?? '').toLowerCase();

  // MIME-first
  if (mime.includes('pdf') || ext === 'pdf') return { Icon: IconFileTypePdf, color: 'red' };
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
    return { Icon: IconPhoto, color: 'green' };
  }
  if (mime.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) {
    return { Icon: IconVideo, color: 'grape' };
  }
  if (mime.startsWith('audio/') || ['mp3', 'wav', 'm4a', 'ogg', 'flac'].includes(ext)) {
    return { Icon: IconFileMusic, color: 'pink' };
  }
  if (mime.includes('wordprocessingml') || ext === 'docx') return { Icon: IconFileTypeDocx, color: 'blue' };
  if (mime.includes('msword') || ext === 'doc') return { Icon: IconFileTypeDoc, color: 'blue' };
  if (
    mime.includes('spreadsheetml') ||
    mime.includes('ms-excel') ||
    ['xls', 'xlsx'].includes(ext)
  ) {
    return { Icon: IconFileTypeXls, color: 'teal' };
  }
  if (mime.includes('csv') || ext === 'csv') return { Icon: IconFileTypeCsv, color: 'teal' };
  if (mime.includes('presentation') || ['ppt', 'pptx'].includes(ext)) {
    return { Icon: IconFileTypePpt, color: 'orange' };
  }
  if (mime.includes('zip') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    return { Icon: IconFileTypeZip, color: 'gray' };
  }
  if (mime.startsWith('text/') || ['txt', 'md', 'log'].includes(ext)) {
    return { Icon: IconFileTypeTxt, color: 'gray' };
  }

  return { Icon: IconFile, color: 'blue' };
};

export const initials = (name?: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const greetingForHour = (date: Date = new Date()): string => {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  if (hour >= 17 && hour < 22) return 'Good evening';
  return 'Hi';
};

/**
 * Strip HTML tags to plain text — for fields authored in a rich-text editor
 * (e.g. leave reason / comment) that should display as plain text rather than
 * raw `<p>…</p>` markup. Block-level tags become line breaks so multi-paragraph
 * content stays readable; render the result with `white-space: pre-wrap`.
 */
export const stripHtml = (html?: string | null): string => {
  if (!html) return '';
  const withBreaks = html
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n');
  // DOMParser builds an inert document — assigning innerHTML to an element would load
  // images and fire `onerror` handlers embedded in the stored content.
  const text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ?? '';
  return text.replace(/\n{3,}/g, '\n\n').trim();
};
